import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SDAGoBack from "./SDAGoBack";
import SDASteps from "./SDASteps";
import { SDATestModeBanner, SDATestSkipButton } from "./sdaTestMode";
import { useAuth } from "../../contexts/AuthContext";
import {
  DOCUMENT_BILLING_AUTHORIZATION_ID,
  DOCUMENT_LETTER_OF_CREDIT_ID,
  signDocument,
} from "../../api/services";
import BillingAuthorization from "./documents/BillingAuthorization";
import LetterOfCredit, {
  LetterOfCreditData,
} from "./documents/LetterOfCredit";
import SignDocumentModal from "./documents/SignDocumentModal";
import { useSDAInvite } from "./SDAContext";

interface DocumentTile {
  documentId: number;
  title: string;
  description: string;
  render: () => React.ReactNode;
}

const SDASignatures: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { invite, quote } = useSDAInvite();

  const expectedName = user ? `${user.firstName} ${user.lastName}` : "";

  // Per spec: don't restore signatures from a previous session — require
  // fresh signatures every time the user reaches this step.
  const [signed, setSigned] = useState<Record<number, boolean>>({});
  const [openDocId, setOpenDocId] = useState<number | null>(null);

  // The Letter of Credit Number (policyName) is only assigned at policy
  // creation. Quote.total fills in the Non-Refundable Issuing Fee.
  const locData: LetterOfCreditData = {
    tenantName: expectedName,
    propertyManager: invite?.account?.companyName,
    propertyName: invite?.unit?.property?.name,
    coverageAmount: invite?.coverageAmount ?? undefined,
    monthlyRentAmount: invite?.monthlyRentAmount,
    coverageStartDate: invite?.leaseStartDate ?? undefined,
    coverageEndDate: invite?.leaseEndDate ?? undefined,
    issuingFee: quote?.total,
  };

  const tiles: DocumentTile[] = [
    {
      documentId: DOCUMENT_BILLING_AUTHORIZATION_ID,
      title: t("sda.signatures.billingTitle"),
      description: t("sda.signatures.billingDescription"),
      render: () => <BillingAuthorization />,
    },
    {
      documentId: DOCUMENT_LETTER_OF_CREDIT_ID,
      title: t("sda.signatures.locTitle"),
      description: t("sda.signatures.locDescription"),
      render: () => <LetterOfCredit data={locData} />,
    },
  ];

  const allSigned = tiles.every((t) => signed[t.documentId]);
  const openTile = tiles.find((t) => t.documentId === openDocId) ?? null;

  const handleConfirm = async (typedName: string) => {
    if (!openTile) return;
    await signDocument(openTile.documentId, typedName, {
      inviteUuid: uuid,
    });
    setSigned((prev) => ({ ...prev, [openTile.documentId]: true }));
    setOpenDocId(null);
  };

  return (
    <div>
      <SDAGoBack />

      <SDATestModeBanner />

      <SDASteps current={6} />

      <div className="rounded-xl bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-4">
          <h3 className="text-base font-semibold text-primary">
            {t("sda.signatures.title")}
          </h3>
          <p className="mt-0.5 text-sm text-muted">
            {t("sda.signatures.subtitle")}
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {tiles.map((tile) => {
              const isSigned = !!signed[tile.documentId];
              return (
                <button
                  key={tile.documentId}
                  type="button"
                  onClick={() => setOpenDocId(tile.documentId)}
                  className={`flex items-start gap-4 rounded-xl border p-5 text-left transition-colors cursor-pointer ${
                    isSigned
                      ? "border-success/40 bg-success/5 hover:bg-success/10"
                      : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                >
                  <div
                    className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs ${
                      isSigned
                        ? "bg-success text-white"
                        : "border border-gray-300 bg-white"
                    }`}
                  >
                    {isSigned ? (
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : null}
                  </div>
                  <div className="grow">
                    <h5 className="text-sm font-semibold text-primary">
                      {tile.title}
                    </h5>
                    <p className="text-xs text-muted">{tile.description}</p>
                    <p className="mt-2 text-xs font-medium text-info">
                      {isSigned
                        ? t("sda.signatures.signed")
                        : t("sda.signatures.clickToSign")}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-4">
          <SDATestSkipButton
            onSkip={() => navigate(`/sda/${uuid}/payment`)}
          />
          <button
            className="rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            disabled={!allSigned}
            onClick={() => navigate(`/sda/${uuid}/payment`)}
          >
            {t("sda.signatures.continue")}
          </button>
        </div>
      </div>

      <SignDocumentModal
        open={openTile !== null}
        title={openTile?.title ?? ""}
        expectedName={expectedName}
        alreadySigned={openTile ? !!signed[openTile.documentId] : false}
        onClose={() => setOpenDocId(null)}
        onConfirm={handleConfirm}
      >
        {openTile?.render()}
      </SignDocumentModal>
    </div>
  );
};

export default SDASignatures;
